import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Lock, Loader2, Phone, Mail, MapPin, Car, RefreshCw, LogOut } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent } from "./components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "contacted", "quoted", "closed"];
const STATUS_STYLES = {
  new: "bg-sky-100 text-sky-700",
  contacted: "bg-amber-100 text-amber-700",
  quoted: "bg-purple-100 text-purple-700",
  closed: "bg-slate-200 text-slate-600",
};

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_password", password);
      onLogin(password);
    } catch (e) {
      toast.error("Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-8">
          <div className="w-14 h-14 bg-sky-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-center mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>Panel SydneyShine</h1>
          <p className="text-slate-500 text-center text-sm mb-6">Introduce la contraseña para ver las solicitudes</p>
          <Label htmlFor="pw">Contraseña</Label>
          <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••••" data-testid="admin-password-input" />
          <Button className="w-full mt-4 bg-sky-500 hover:bg-sky-600" onClick={submit} disabled={loading || !password} data-testid="admin-login-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const QuoteCard = ({ quote, password, onUpdated }) => {
  const [status, setStatus] = useState(quote.status || "new");
  const [price, setPrice] = useState(quote.quoted_price ?? "");
  const [adminNotes, setAdminNotes] = useState(quote.admin_notes ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/quotes/${quote.id}`,
        { status, quoted_price: price === "" ? null : parseFloat(price), admin_notes: adminNotes },
        { headers: { "X-Admin-Password": password } });
      toast.success("Guardado");
      onUpdated();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const date = new Date(quote.created_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });

  return (
    <Card className="overflow-hidden" data-testid={`quote-card-${quote.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{quote.name}</h3>
            <p className="text-xs text-slate-400">{date}</p>
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_STYLES[quote.status] || STATUS_STYLES.new}`}>
            {quote.status}
          </span>
        </div>

        <div className="space-y-1.5 text-sm text-slate-700 mb-4">
          <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> <a href={`tel:${quote.phone}`} className="text-sky-600">{quote.phone}</a></p>
          {quote.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {quote.email}</p>}
          <p className="flex items-center gap-2"><Car className="w-4 h-4 text-slate-400" /> {quote.vehicle} <span className="text-slate-400">·</span> <span className="font-medium">{quote.vehicle_type}</span></p>
          {(quote.address || quote.suburb) && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {[quote.address, quote.suburb].filter(Boolean).join(", ")}</p>}
          {quote.service_interest && <p className="text-slate-600"><span className="text-slate-400">Servicio:</span> {quote.service_interest}</p>}
          {quote.notes && <p className="text-slate-600 bg-slate-50 rounded-lg p-2 mt-2"><span className="text-slate-400">Notas:</span> {quote.notes}</p>}
        </div>

        {quote.photo_urls && quote.photo_urls.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mb-4" data-testid={`quote-photos-${quote.id}`}>
            {quote.photo_urls.map((u, i) => (
              <a key={i} href={`${BACKEND_URL}${u}`} target="_blank" rel="noopener noreferrer">
                <img src={`${BACKEND_URL}${u}`} alt="vehicle" className="w-full h-20 object-cover rounded-lg border border-slate-200 hover:opacity-80 transition-opacity" />
              </a>
            ))}
          </div>
        )}

        <a href={`https://wa.me/${quote.phone.replace(/[^0-9]/g, "").replace(/^0/, "61")}`} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="w-full mb-4 border-green-300 text-green-700 hover:bg-green-50">Responder por WhatsApp</Button>
        </a>

        <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Estado</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`status-select-${quote.id}`}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Precio (AUD)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" className="h-9" data-testid={`price-input-${quote.id}`} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notas internas</Label>
            <Input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Notas privadas..." className="h-9" data-testid={`admin-notes-${quote.id}`} />
          </div>
        </div>
        <Button className="w-full mt-3 bg-slate-900 hover:bg-slate-800" size="sm" onClick={save} disabled={saving} data-testid={`save-quote-${quote.id}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}
        </Button>
      </CardContent>
    </Card>
  );
};

const AdminPage = () => {
  const [password, setPassword] = useState(localStorage.getItem("admin_password") || "");
  const [authed, setAuthed] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quotes`, { headers: { "X-Admin-Password": pw } });
      setQuotes(res.data);
      setAuthed(true);
    } catch (e) {
      setAuthed(false);
      localStorage.removeItem("admin_password");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password) load(password);
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("admin_password");
    setAuthed(false);
    setPassword("");
  };

  if (!authed) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <Login onLogin={(pw) => { setPassword(pw); load(pw); }} />
      </>
    );
  }

  const counts = quotes.reduce((a, q) => { a[q.status] = (a[q.status] || 0) + 1; return a; }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Toaster position="top-center" richColors />
      <header className="bg-slate-900 text-white py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Solicitudes de presupuesto</h1>
            <p className="text-slate-400 text-sm">{quotes.length} en total · {counts.new || 0} nuevas</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-slate-600 text-white hover:bg-slate-800" onClick={() => load(password)} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" variant="outline" className="border-slate-600 text-white hover:bg-slate-800" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {loading && quotes.length === 0 ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="no-quotes">Aún no hay solicitudes.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {quotes.map(q => <QuoteCard key={q.id} quote={q} password={password} onUpdated={() => load(password)} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
